/**
 * Cloudinary URL utilities for the frontend
 * Handles URL optimization and detection
 */

// Check if a URL is a Cloudinary URL
export const isCloudinaryUrl = (url) => {
  if (!url) return false;
  return url.includes('res.cloudinary.com') || url.includes('cloudinary.com');
};

// Check if a URL is unstable (non-persistent)
export const isUnstableUrl = (url) => {
  if (!url) return true;
  
  const unstablePatterns = [
    '/api/uploads/',
    '.preview.emergentagent.com',
    'customer-assets.emergentagent.com',
    '/uploads/'
  ];
  
  return unstablePatterns.some(pattern => url.includes(pattern));
};

/**
 * Get optimized Cloudinary URL with transformations
 * @param {string} url - The original Cloudinary URL
 * @param {object} options - Transformation options
 * @returns {string} - Optimized URL
 */
export const getOptimizedCloudinaryUrl = (url, options = {}) => {
  if (!isCloudinaryUrl(url)) return url;
  
  const {
    width = 1200,
    quality = 'auto',
    format = 'auto',
    crop = null
  } = options;
  
  // Build transformation string
  let transforms = [`f_${format}`, `q_${quality}`];
  
  if (width) {
    transforms.push(`w_${width}`);
  }
  
  if (crop) {
    transforms.push(`c_${crop}`);
  }
  
  const transformString = transforms.join(',');
  
  // Check if URL already has transformations
  if (url.includes('/upload/f_') || url.includes('/upload/w_') || url.includes('/upload/q_')) {
    return url;
  }
  
  // Insert transformations after /upload/
  return url.replace('/upload/', `/upload/${transformString}/`);
};

/**
 * Get image URL with automatic optimization based on context
 * @param {string} url - Original image URL
 * @param {string} context - 'hero' | 'gallery' | 'thumbnail' | 'full'
 * @returns {string} - Optimized URL
 */
export const getContextualImageUrl = (url, context = 'gallery') => {
  if (!url) return '';
  
  // If not Cloudinary, return as-is
  if (!isCloudinaryUrl(url)) return url;
  
  const contextSettings = {
    hero: { width: 1920, quality: 'auto:best' },
    gallery: { width: 900, quality: 'auto:good' },
    thumbnail: { width: 400, quality: 'auto:eco', crop: 'fill' },
    full: { width: 2400, quality: 'auto:best' }
  };
  
  const settings = contextSettings[context] || contextSettings.gallery;
  return getOptimizedCloudinaryUrl(url, settings);
};

// Fallback placeholder for broken images
export const FALLBACK_PLACEHOLDER = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=400&q=60';
